// Built-in lab tests (SPEC.md §4.3 lab_tests), from the SaaS default panels (migration 005 seeds)
// plus the post-dialysis urea needed for URR, TSAT and HbA1c. These live in code; `lab_tests` rows
// hold only the user's changes (id `std-<code>`) and their own custom tests. Ranges are typical adult
// ranges: the user's lab's printed range takes precedence, and every range can be edited.
// Critical values are common laboratory alert limits, used only for the "very high/low" flag.

export const LAB_PANELS = [
  { id: 'CBC', name: 'Blood count (CBC)' },
  { id: 'RFT', name: 'Kidney function' },
  { id: 'ELECTROLYTES', name: 'Electrolytes' },
  { id: 'MIN_IRON', name: 'Minerals, iron & inflammation' },
  { id: 'SPECIAL_CHEM', name: 'Special chemistry' },
  { id: 'LFT', name: 'Liver function' },
  { id: 'HEPATITIS', name: 'Hepatitis' },
  { id: 'CUSTOM', name: 'My tests' }
] as const;

export type PanelId = (typeof LAB_PANELS)[number]['id'];

export interface CatalogTest {
  code: string;
  panel: string;
  name: string;
  unit: string | null;
  result_type: 'numeric' | 'qualitative' | 'text';
  normal_min: number | null;
  normal_max: number | null;
  critical_low: number | null;
  critical_high: number | null;
  sort_order: number;
}

type Row = [code: string, name: string, unit: string | null, min: number | null, max: number | null, critLow?: number | null, critHigh?: number | null];

function panel(id: PanelId, rows: Row[], resultType: CatalogTest['result_type'] = 'numeric'): CatalogTest[] {
  return rows.map(([code, name, unit, min, max, critLow = null, critHigh = null], index) => ({
    code,
    panel: id,
    name,
    unit,
    result_type: resultType,
    normal_min: min,
    normal_max: max,
    critical_low: critLow,
    critical_high: critHigh,
    sort_order: (index + 1) * 10
  }));
}

export const DEFAULT_LAB_TESTS: CatalogTest[] = [
  ...panel('CBC', [
    ['HB', 'Hemoglobin', 'g/dL', 13, 17, 7, null],
    ['HCT', 'Hematocrit', '%', 40, 50],
    ['WBC', 'WBC count', '/mm3', 4000, 10000, 2000, 30000],
    ['PLT', 'Platelets', '/mm3', 150000, 410000, 20000, null],
    ['RBC', 'RBC count', 'mil/mm3', 4.5, 5.5],
    ['MCV', 'MCV', 'fL', 83, 101],
    ['MCH', 'MCH', 'pg', 27, 32],
    ['MCHC', 'MCHC', 'g/dL', 31.5, 34.5],
    ['RDW', 'RDW-CV', '%', 11, 16],
    ['NEUT', 'Neutrophils', '%', 40, 70],
    ['LYMPH', 'Lymphocytes', '%', 25, 45],
    ['MONO', 'Monocytes', '%', 2, 12],
    ['EOS', 'Eosinophils', '%', 1, 5],
    ['BASO', 'Basophils', '%', 0, 1]
  ]),
  ...panel('RFT', [
    ['UREA', 'Blood urea (before dialysis)', 'mg/dL', 10, 50],
    ['UREA_POST', 'Blood urea (after dialysis)', 'mg/dL', null, null],
    ['CREAT', 'Serum creatinine', 'mg/dL', 0.4, 1.3],
    ['BUN', 'BUN', 'mg/dL', 5, 24],
    ['URIC', 'Uric acid', 'mg/dL', 3.7, 7.7],
    ['EGFR', 'eGFR', 'mL/min/1.73m²', 60, null]
  ]),
  ...panel('ELECTROLYTES', [
    ['K', 'Potassium', 'mmol/L', 3.5, 5.3, 2.8, 6.5],
    ['NA', 'Sodium', 'mmol/L', 136, 145, 120, 160],
    ['CL', 'Chloride', 'mmol/L', 98, 107],
    ['HCO3', 'Bicarbonate', 'mmol/L', 23, 31, 12, null],
    ['AG', 'Anion gap', 'mmol/L', 8, 16]
  ]),
  ...panel('MIN_IRON', [
    ['CA', 'Calcium', 'mg/dL', 8.4, 10.2, 6.5, 13],
    ['PHOS', 'Phosphorus', 'mg/dL', 2.4, 4.7],
    ['ALB', 'Albumin', 'g/dL', 3.5, 5.2],
    ['IRON', 'Iron', 'µg/dL', 65, 170],
    ['TIBC', 'TIBC', 'µg/dL', 250, 450],
    ['TSAT', 'Transferrin saturation (TSAT)', '%', 20, 50],
    ['CRP', 'CRP', 'mg/L', null, 5]
  ]),
  ...panel('SPECIAL_CHEM', [
    ['PTH', 'PTH (intact)', 'pg/mL', 15, 68],
    ['FERRITIN', 'Ferritin', 'ng/mL', 20, 275],
    ['HBA1C', 'HbA1c', '%', null, 5.7]
  ]),
  ...panel('LFT', [
    ['TBIL', 'Total bilirubin', 'mg/dL', 0.2, 1.1],
    ['DBIL', 'Direct bilirubin', 'mg/dL', 0, 0.3],
    ['IBIL', 'Indirect bilirubin', 'mg/dL', 0.1, 0.8],
    ['AST', 'SGOT (AST)', 'IU/L', 9, 40],
    ['ALT', 'SGPT (ALT)', 'IU/L', 5, 50],
    ['ALP', 'Alkaline phosphatase', 'IU/L', 56, 167],
    ['GGT', 'Gamma GT', 'IU/L', null, 69]
  ]),
  ...panel('HEPATITIS', [
    ['HBSAG', 'Hepatitis B surface antigen (HBsAg)', null, null, null],
    ['HCV', 'Hepatitis C antibody (anti-HCV)', null, null, null]
  ], 'qualitative'),
  ...panel('HEPATITIS', [
    ['HBSAG_SCO', 'HBsAg S/CO', 'S/CO', null, 1],
    ['HCV_SCO', 'Anti-HCV S/CO', 'S/CO', null, 1]
  ]).map((test) => ({ ...test, sort_order: test.sort_order + 100 }))
];

/** Tests shown as trends on the Labs screen and in the doctor report (SPEC.md §9). */
export const KEY_TEST_CODES = ['HB', 'K', 'PHOS', 'CA', 'ALB', 'PTH', 'UREA'];

export const QUALITATIVE_OPTIONS = ['Non-reactive', 'Borderline', 'Reactive'];

export const stdTestId = (code: string) => `std-${code}`;
